import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { AppBar, CircularProgress, Box, useMediaQuery, Theme, TextField, Button } from '@mui/material';
import NavBar from '../Navigation/Navigation';
import { format } from 'date-fns';
import { useTheme } from '@mui/material/styles';

interface PriceChangeDetails {
  Location_ID: string;
  CreatedLocation: string;
  Price_Change_No: string;
  Notes: string;
  Price_Change_Name: string;
  Prepared_Date: string | "";
  Approved_Date: string | "";
  Applied_Date:string | "";
  Start_DateTime: string | "";
  End_DateTime: string | "";
  Status: string;
  Price_Change_Type: string;
  Price_Change_Option: string;
  Total_Qty: number;  
}

const PriceChangeView: React.FC = () => {
  const location = useLocation();
  const [priceChangeData, setPriceChangeData] = useState<PriceChangeDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [rowCount, setRowCount] = useState<number>(0);
  const navigate = useNavigate();
  const [priceChangeNo, setPriceChangeNo] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const theme = useTheme();
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const params = new URLSearchParams(location.search);
  const status = params.get('status') || 'sent';

  // Debounced fetch function
  const fetchTransfers = async (page: number, pageSize: number) => {
    setLoading(true);
    try {
      const response = await fetch('/pc/priceDropView', {
        method: 'POST',
        credentials:'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page, pageSize, priceChangeNo }),
      });
      const data = await response.json();
      setPriceChangeData(data.records);
      setRowCount(data.totalRecords);
    } catch (error) {
      console.error('Error fetching transfer data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchTransfers(paginationModel.page, paginationModel.pageSize);
    }, 1000); // 1000ms debounce

    return () => clearTimeout(handler);
  }, [paginationModel, priceChangeNo]);

  const handleTransferNoSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPriceChangeNo(e.target.value);
    setPaginationModel({ page: 0, pageSize: 50 });
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'MM-dd-yyyy hh:mm a');
  };

  const columns: GridColDef[] = [
    { field: 'Location_ID', headerName: 'Location', width: 100 },
    { field: 'createdLocation', headerName: 'CreatedLoc', width: 130 },
    { field: 'Price_Change_No', headerName: 'PC_No', width: 100 },
    { field: 'Price_Change_Name', headerName: 'PC_Name', width: 130 },
    
      {
      field: 'Approved_Date',
      headerName: 'Approved_Date',
      width: 190,
      valueGetter: (params) => formatDate(params.row.Approved_Date),
    },
    {
      field: 'Applied_Date',
      headerName: 'Applied_Date',
      width: 190,
      valueGetter: (params) => formatDate(params.row.Applied_Date),
    },
    {
      field: 'Start_DateTime',
      headerName: 'StartDate',
      width: 190,
      valueGetter: (params) => formatDate(params.row.Start_DateTime),
    },
    
     { field: 'Status', headerName: 'Status'},
      { field: 'Price_Change_Type', headerName: 'PC_Type',  width: 120 },
       { field: 'Price_Change_Option', headerName: 'PC_Option',  width: 130 },
        { field: 'Total_Qty', headerName: 'TotalQty', width: 90  },
    { field: 'Notes', headerName: 'Notes',  flex:1 },
  ];

  const handleRowClick = (params: any) => {
    const priceChangeNo = params.row.Price_Change_No;
    //const SendingLocation = params.row.SendingLocation;
    //const receivingLocation = params.row.receivingLocation;
    
    navigate(`/pc/priceDropItems/${priceChangeNo}`);
  };

  return (
    <>
      <AppBar position='sticky'>
        <NavBar />
      </AppBar>
      <Box
        sx={{
          height: isSmallScreen ? '80vh' : '90vh', 
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
           sx={{
            height: isSmallScreen ? '70vh' : '82vh', // Adjust height based on screen size
            width: '100%',
            '& .MuiDataGrid-root': {
              border: '1px solid #ccc', // Border for the grid
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#FFFFF0', // Header row background color
              fontSize: '1.0rem', // Header font size
              fontWeight: 'bold', // Header font weight
              borderBottom: '1px solid #ccc',
              
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #ccc', // Add border between cells
              borderRight: '1px solid #ccc',
              fontSize: '14px',
              color: 'black'
            },
            '& .MuiDataGrid-cell:last-child': {
              borderRight: 'none', // Remove right border for the last cell in the row
            },
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: '#D3D3D3',
              fontSize: '1rem', // Odd row background color
              cursor: 'pointer'
            },
            '& .MuiDataGrid-row:nth-of-type(even)': {
              backgroundColor: '#FAF9F6', // Even row background color
              fontSize:'1rem',
              cursor:'pointer'
            },
          }}
        >
          <DataGrid
            rows={priceChangeData}
            columns={columns}
            pagination
            pageSizeOptions={[50]}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            getRowId={(row) => row.Price_Change_No}
            loading={loading}
            onRowClick={handleRowClick}
          />
        </Box>
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #ccc',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
          }}
        >
          <TextField
            label="Search by Transfer Number"
            variant="outlined"
            value={priceChangeNo || ''}
            onChange={handleTransferNoSearch}
            sx={{width:'100%' }}
          />
         
        </Box>
      </Box>
    </>
  );
};

export default PriceChangeView;
